import React, { useState, useEffect } from 'react';
import { api } from '../../api';

interface InactiveBotDetails {
  id: string;
  identificador: string;
  bot_name: string;
  last_interaction_date: string | null;
  inactivity_category: 'never_active' | 'recently_inactive' | 'inactive_medium' | 'long_inactive';
  days_inactive: number | null;
}

interface InactiveBotsData {
  success: boolean;
  reference_dates: {
    today: string;
    week_ago: string;
    month_start: string;
  };
  counts: {
    total_bots: number;
    inactive_today: number;
    inactive_this_week: number;
    inactive_this_month: number;
  };
  inactive_bots: {
    daily: InactiveBotDetails[];
    weekly: InactiveBotDetails[];
    monthly: InactiveBotDetails[];
  };
}

const BotAlertsPanel: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [inactiveBotsData, setInactiveBotsData] = useState<InactiveBotsData | null>(null);
  const [activeTab, setActiveTab] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);

  useEffect(() => {
    const fetchInactiveBots = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.getInactiveBots();
        
        if (response && response.success) {
          setInactiveBotsData(response);
        } else {
          setError('Falha ao carregar dados de bots inativos');
        }
      } catch (err) {
        console.error('Erro ao buscar bots inativos:', err);
        setError('Erro ao carregar dados de bots inativos');
      } finally {
        setLoading(false);
      }
    };

    fetchInactiveBots();
    
    // Refresh data every 5 minutes
    const interval = setInterval(fetchInactiveBots, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Helper to format time period for display
  const formatTimePeriod = (period: 'daily' | 'weekly' | 'monthly') => {
    switch (period) {
      case 'daily': return 'Hoje';
      case 'weekly': return 'Esta Semana';
      case 'monthly': return 'Este Mês';
      default: return '';
    }
  };

  // Handle export to CSV
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      await api.exportInactiveBotsCSV(activeTab);
      setExportSuccess(true);
      
      // Reset success status after 2 seconds
      setTimeout(() => {
        setExportSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
    } finally {
      setExporting(false);
    }
  };

  // Helper to format date for display
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper to get color for inactivity category
  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'never_active': return 'bg-gray-600';
      case 'recently_inactive': return 'bg-yellow-600';
      case 'inactive_medium': return 'bg-orange-600';
      case 'long_inactive': return 'bg-red-600';
      default: return 'bg-gray-600';
    }
  };

  // Helper to get text for inactivity category
  const getCategoryText = (category: string): string => {
    switch (category) {
      case 'never_active': return 'Nunca ativo';
      case 'recently_inactive': return 'Recentemente inativo';
      case 'inactive_medium': return 'Inativo (médio prazo)';
      case 'long_inactive': return 'Inativo (longo prazo)';
      default: return 'Desconhecido';
    }
  };

  if (loading) {
    return (
      <div className="bot-alerts-panel card">
        <h3 className="section-title with-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Alertas de Bots
        </h3>
        <div className="p-4 flex items-center justify-center">
          <div className="animate-pulse text-center">
            <div className="h-4 w-32 bg-gray-700 rounded mx-auto"></div>
            <div className="mt-2 h-3 w-24 bg-gray-700 rounded mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !inactiveBotsData) {
    return (
      <div className="bot-alerts-panel card">
        <h3 className="section-title with-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Alertas de Bots
        </h3>
        <div className="p-4 text-center text-red-400">
          <p>Erro ao carregar alertas de bots. Tente novamente mais tarde.</p>
          {error && <p className="text-sm mt-2">{error}</p>}
        </div>
      </div>
    );
  }

  const currentTabData = inactiveBotsData.inactive_bots[activeTab] || [];
  const { counts } = inactiveBotsData;
  
  // Helper to get count for current tab
  const getCurrentTabCount = () => {
    switch (activeTab) {
      case 'daily': return counts.inactive_today;
      case 'weekly': return counts.inactive_this_week;
      case 'monthly': return counts.inactive_this_month;
      default: return 0;
    }
  };

  return (
    <div className="bot-alerts-panel card">
      <h3 className="section-title with-icon flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        Alertas de Bots
      </h3>
      
      {/* Tabs for time periods */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          Hoje
          <span className="ml-2 px-2 py-1 text-xs rounded bg-yellow-800">{counts.inactive_today}</span>
        </button>
        <button 
          className={`tab ${activeTab === 'weekly' ? 'active' : ''}`}
          onClick={() => setActiveTab('weekly')}
        >
          Semana
          <span className="ml-2 px-2 py-1 text-xs rounded bg-orange-800">{counts.inactive_this_week}</span>
        </button>
        <button 
          className={`tab ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('monthly')}
        >
          Mês
          <span className="ml-2 px-2 py-1 text-xs rounded bg-red-800">{counts.inactive_this_month}</span>
        </button>
        
        {/* Export CSV Button */}
        <button 
          className={`ml-auto export-button ${exporting ? 'exporting' : ''} ${exportSuccess ? 'success' : ''}`}
          onClick={handleExportCSV}
          disabled={exporting}
        >
          {exportSuccess ? (
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-4 w-4" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          ) : (
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-4 w-4" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 10v6m0 0l-3-3m3 3l3-3m-9 9h12a2 2 0 002-2V7a2 2 0 00-2-2h-3.5L10 2H4a2 2 0 00-2 2v13a2 2 0 002 2z" 
              />
            </svg>
          )}
          <span>
            {exporting ? 'Exportando...' : exportSuccess ? 'Concluído!' : 'Exportar CSV'}
          </span>
        </button>
      </div>
      
      <div className="p-4">
        <div className="summary-box mb-4">
          <p>
            <strong>{getCurrentTabCount()}</strong> bots sem atividade em <strong>{formatTimePeriod(activeTab)}</strong> 
            {activeTab !== 'daily' && <span> de um total de {counts.total_bots} bots</span>}
          </p>
        </div>
        
        {currentTabData.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>Nenhum bot inativo {formatTimePeriod(activeTab).toLowerCase()}</p>
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="bot-alerts-table w-full">
              <thead>
                <tr>
                  <th>Bot</th>
                  <th>Última Atividade</th>
                  <th>Dias Inativo</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentTabData.map((bot) => (
                  <tr key={bot.id}>
                    <td className="bot-name">
                      {bot.bot_name || bot.identificador || `Bot ${bot.id.substring(0, 8)}`}
                    </td>
                    <td>{formatDate(bot.last_interaction_date)}</td>
                    <td>{bot.days_inactive === null ? '-' : bot.days_inactive}</td>
                    <td>
                      <span className={`category-badge ${getCategoryColor(bot.inactivity_category)}`}>
                        {getCategoryText(bot.inactivity_category)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BotAlertsPanel; 